import React from 'react'
import { useNewTranslation } from 'hooks'
import ObjectiveIcon from '../ObjectiveIcon'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import RectangleButton from 'particles/Button/RectangleButton'
import { Theme } from 'particles/themes'
import { ObjectiveType } from 'state/campaignTemplates'
import Icon from 'particles/Icon'

interface TemplateCardProps {
    onClick?: () => void
    title: string
    description: string
    subTitle: string
    isDisabled?: boolean
    isSelected?: boolean
    isReadOnly?: boolean
    showButton?: boolean
    icon: ObjectiveType
    isCurrentTemplate?: boolean
    dataTestId: string
}

const TemplateCard: React.FC<TemplateCardProps> = ({
    title,
    description,
    subTitle,
    isDisabled = false,
    isSelected = false,
    showButton = false,
    isReadOnly = false,
    icon,
    onClick,
    isCurrentTemplate = false,
    dataTestId,
}) => {
    const [t] = useNewTranslation()
    const isShowCta = showButton && !isReadOnly
    return (
        <TemplateCardContainer
            isDisabled={isDisabled}
            isSelected={isSelected}
            isReadOnly={isReadOnly}
            onClick={onClick}
            data-testid={dataTestId}
        >
            <ObjectiveIcon type={icon} isDisabled={isDisabled} />
            <div className="content-container">
                <div className="content">
                    <div className="title">
                        <Text scale="bodyM700" color={isDisabled ? 'disable' : 'primary'}>
                            {title}
                        </Text>
                        <Text scale="bodyS500" color={isDisabled ? 'disable' : 'secondary'}>
                            {subTitle}
                        </Text>
                        {isCurrentTemplate && (
                            <Tag>
                                <Icon size="16px" name={'fileText'} color={'actionInverse'} />
                                <Text scale="caption500" color={'actionInverse'}>
                                    {t(
                                        'campaign.template.step1.templateSelection.templateCard.currentTemplateTag.text',
                                    )}
                                </Text>
                            </Tag>
                        )}
                    </div>

                    <Text scale="bodyS400" color={isDisabled ? 'disable' : 'secondary'}>
                        {description}
                    </Text>
                </div>
                {isShowCta && (
                    <RectangleButton
                        type="submit"
                        isDisabled={isDisabled}
                        className="cta-button"
                        label={t('campaign.template.step1.templateSelection.templateCard.PrimaryButton.text')}
                    />
                )}
            </div>
        </TemplateCardContainer>
    )
}

export default TemplateCard

type TemplateViewState = Pick<TemplateCardProps, 'isDisabled' | 'isReadOnly' | 'isSelected'>

const getBorderColor = (theme: Theme, config: TemplateViewState, defaultValue: string) => {
    const { isDisabled, isReadOnly, isSelected } = config
    if (isDisabled) {
        return theme.colors.border.disable
    }

    if (isReadOnly) {
        return theme.colors.border.actionActive
    }

    if (isSelected) {
        return theme.colors.border.action
    }

    return defaultValue
}

const TemplateCardContainer = styled.div<TemplateViewState>`
    box-sizing: border-box;
    font-family: inherit;
    width: 378px;
    min-height: 120px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.surface.primary};
    border: 2px solid
        ${({ theme, isSelected, isDisabled, isReadOnly }) =>
            getBorderColor(theme, { isDisabled, isReadOnly, isSelected }, theme.colors.border.primary)};
    padding: 12px;
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};

    &:hover {
        border-color: ${({ theme, isSelected, isDisabled, isReadOnly }) =>
            getBorderColor(theme, { isDisabled, isReadOnly, isSelected }, theme.colors.border.actionFocus)};
    }

    .content-container {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[300]};

        .title {
            display: flex;
            flex-direction: column;
            gap: ${({ theme }) => theme.spacing[100]};
        }

        .content {
            display: flex;
            flex: 1;
            gap: ${({ theme }) => theme.spacing[200]};
            flex-direction: column;
        }

        .cta-button {
            width: fit-content;
            margin-left: auto;
            margin-right: 0px;
        }
    }
`

const Tag = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 4px;
    align-items: center;
    height: 24px;
    background-color: ${({ theme }) => theme.colors.surface.positive};
    padding: 0px 6px;
    width: fit-content;
    flex-shrink: 0;
`
